<template>
  <div>
    <SHeader :icon="ICON">
      <template v-slot:title>Capteur {{ $route.params.sensorId }}</template>
    </SHeader>

    <SensorDescription v-if="sensor" :sensor="sensor" />

    <v-card class="my-2 pt-2 px-2">
      <v-card-title>Filtrer</v-card-title>
      <v-card-text>
        <div class="d-flex justify-center align-start">
          <SMenu v-model="datePickerStart">
            <template v-slot:activator="{ on, attrs }">
              <STextField
                label="Date début"
                v-model="startDate"
                :error-messages="$fieldValidator('Date début', $v.startDate)"
                @input="$v.startDate.$touch()"
                @blur="$v.startDate.$touch()"
                v-on="on"
                v-bind="attrs"
                readonly
                date
              />
            </template>
            <SDatePicker v-model="startDate" />
          </SMenu>

          <SMenu v-model="datePickerEnd">
            <template v-slot:activator="{ on, attrs }">
              <STextField
                label="Date fin"
                v-model="endDate"
                :error-messages="$fieldValidator('Date fin', $v.endDate)"
                @input="$v.endDate.$touch()"
                @blur="$v.endDate.$touch()"
                v-on="on"
                v-bind="attrs"
                readonly
                date
              />
            </template>
            <SDatePicker v-model="endDate" />
          </SMenu>
        </div>
      </v-card-text>
      <v-card-actions>
        <SButton
          :loading="loading"
          :disabled="$v.$invalid"
          raised
          success
          @click="updateData"
        >
          Valider
        </SButton>
      </v-card-actions>
    </v-card>

    <v-row>
      <v-col cols="12" sm="12" md="12">
        <AreaChart
          v-if="getAqi.length > 0"
          title="Qualité d'air"
          icon="fa-wind"
          :chart-data="getAqi"
          color="#5e569b"
        />
      </v-col>

      <v-col cols="12" sm="12" md="6">
        <AreaChart
          v-if="getHumidity.length > 0"
          title="Humidité"
          icon="fa-droplet"
          :chart-data="getHumidity"
          color="#54bebe"
        />
      </v-col>

      <v-col cols="12" sm="12" md="6">
        <AreaChart
          v-if="getPressure.length > 0"
          title="Pression"
          icon="fa-fan"
          :chart-data="getPressure"
          color="#d7658b"
        />
      </v-col>

      <v-col cols="12" sm="12" md="12">
        <AreaChart
          v-if="getTemperature.length > 0"
          title="Température"
          icon="fa-temperature-high"
          :chart-data="getTemperature"
          color="#d2980d"
        />
      </v-col>

      <v-col cols="12" sm="12" md="6">
        <AreaChart
          v-if="getPM1.length > 0"
          title="PM1"
          icon="fa-virus"
          :chart-data="getPM1"
          color="#599e94"
        />
      </v-col>

      <v-col cols="12" sm="12" md="6">
        <AreaChart
          v-if="getPM2_5.length > 0"
          title="PM2.5"
          icon="fa-virus"
          :chart-data="getPM2_5"
          color="#503f3f"
        />
      </v-col>

      <v-col cols="12" sm="12" md="6">
        <AreaChart
          v-if="getPM4.length > 0"
          title="PM4"
          icon="fa-virus"
          :chart-data="getPM4"
          color="#df979e"
        />
      </v-col>

      <v-col cols="12" sm="12" md="6">
        <AreaChart
          v-if="getPM10.length > 0"
          title="PM10"
          icon="fa-virus"
          :chart-data="getPM10"
          color="#22a7f0"
        />
      </v-col>

      <v-col cols="12" sm="12" md="6">
        <AreaChart
          v-if="getCov.length > 0"
          title="COV"
          icon="fa-virus"
          :chart-data="getCov"
          color="#22a7f0"
        />
      </v-col>

      <v-col cols="12" sm="12" md="6">
        <AreaChart
          v-if="getCO2.length > 0"
          title="CO2"
          icon="fa-virus"
          :chart-data="getCO2"
          color="#22a7f0"
        />
      </v-col>
    </v-row>

    <SAlert
      v-model="alert"
      :message="message"
      :success="successAlert"
      :error="!successAlert"
      :action-type="actionType"
      @onClose="alert = false"
    />
  </div>
</template>

<script>
import AreaChart from "@/components/sensorCharts/AreaChart";
import SensorDescription from "@/components/sensorCharts/SensorDescription.vue";
import { mapGetters, mapActions } from "vuex";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";

export default {
  name: "SensorCharts",
  mixins: [validationMixin],
  validations: {
    startDate: {
      required,
    },
    endDate: {
      required,
    },
  },
  components: {
    SensorDescription,
    AreaChart,
  },
  data: () => ({
    loading: false,
    datePickerStart: false,
    datePickerEnd: false,
    startDate: null,
    endDate: null,
    ICON: "fa-chart-line",
    dialog: false,
    alert: false,
    successAlert: false,
    message: "",
    actionType: "normal",
    sensor: null,
  }),
  methods: {
    askUpdate(sensor) {
      this.dialog = true;
      this.sensor = sensor;
    },
    openUpdate() {},
    closeDialog() {
      this.dialog = false;
      this.sensor = null;
    },
    openAlert({ success, message }) {
      this.alert = true;
      this.successAlert = success;
      this.message = message;
    },
    async updateData() {
      try {
        const macAddress = this.$route.params.sensorId;
        await this.retrieveSensorData({
          macAddress,
          startDate: this.startDate,
          endDate: this.endDate,
        });
      } catch ({ success, error }) {
        this.openAlert({ success, message: error });
      }
    },
    ...mapActions({
      retrieveSensor: "sensors/retrieveSensor",
      retrieveSensorData: "sensors/retrieveSensorData",
    }),
  },
  computed: {
    ...mapGetters({
      getSensor: "sensors/getSensor",
      getSensors: "sensors/getSensors",
      getTemperature: "sensors/getTemperature",
      getHumidity: "sensors/getHumidity",
      getPressure: "sensors/getPressure",
      getAqi: "sensors/getAqi",
      getPM1: "sensors/getPM1",
      getPM2_5: "sensors/getPM2_5",
      getPM4: "sensors/getPM4",
      getPM10: "sensors/getPM10",
      getCov: "sensors/getCov",
      getCO2: "sensors/getCO2",
    }),
  },
  async created() {
    try {
      const macAddress = this.$route.params.sensorId;
      if (this.getSensors.length == 0) {
        await this.retrieveSensor(macAddress);
        this.sensor = this.getSensor;
      } else {
        this.sensor = this.getSensors.find((s) => s.macAddress == macAddress);
      }
      await this.retrieveSensorData({ macAddress });
    } catch ({ success, error }) {
      this.openAlert({ success, message: error });
    }
  },
};
</script>

<style></style>
